import React from 'react';
import { motion } from 'framer-motion';
import { usePlanningContext } from '../state/PlanningContext';
import { ArrowUpDown, Filter, Aperture, Layers } from 'lucide-react';
import { trackPlanningUpdatesEvent } from '../../../lib/planningUpdatesAnalytics';

// Base toggle button styles and behavior
const BaseToggleButton = ({ 
  isActive, 
  onClick, 
  icon: IconComponent,
  label,
  activeLabel
}) => {
  const buttonClasses = `btn flex items-center gap-2 ${
    isActive
      ? 'bg-[var(--c-accent)] text-[var(--c-bg)] border-[var(--c-accent)] hover:bg-[var(--c-accent-hover)]'
      : 'bg-[var(--c-surface)] text-[var(--c-text-primary)] border-[var(--c-border)] hover:bg-[var(--c-surface-hover)]'
  }`;
  
  return (
    <motion.button
      whileTap={{ scale: 0.97 }}
      className={buttonClasses}
      onClick={onClick}
    >
      {IconComponent && <IconComponent size={16} className="shrink-0" />}
      <span>{isActive && activeLabel ? activeLabel : label}</span>
    </motion.button>
  );
};

// AI Summaries Toggle Button
export const AISummariesToggle = ({ large = false }) => {
  const { showAiSummaries, toggleAiSummaries } = usePlanningContext();
  
  const aiButtonClasses = `btn flex items-center gap-2 ${large ? 'py-3 px-5 text-base' : ''} ${
    showAiSummaries
      ? 'btn--primary'
      : 'bg-[var(--c-surface)] text-[var(--c-text-primary)] border-[var(--c-border)] hover:bg-[var(--c-surface-hover)]'
  }`;

  return (
    <motion.button
      whileTap={{ scale: 0.97 }}
      whileHover={{ scale: 1.03 }}
      className={aiButtonClasses}
      onClick={() => {
        trackPlanningUpdatesEvent('planning_updates_ai_summaries_toggled', {
          placement: large ? 'display_options_large' : 'display_options',
          status: showAiSummaries ? 'disabled' : 'enabled',
          metadata: {
            enabled: !showAiSummaries
          }
        });
        toggleAiSummaries();
      }}
    >
      <Aperture size={large ? 20 : 16} className={`shrink-0 ${showAiSummaries ? 'animate-spin-slow' : ''}`} />
      <span className="font-medium">
        {showAiSummaries ? "AI Summaries: ON" : "AI Summaries: OFF"}
      </span>
    </motion.button>
  );
};

// Sort By Date Toggle Button
export const SortByDateToggle = () => {
  const { sortByDate, setSortByDate } = usePlanningContext();
  
  const handleToggle = () => {
    trackPlanningUpdatesEvent('planning_updates_sort_by_date_toggled', {
      placement: 'display_options',
      status: sortByDate ? 'disabled' : 'enabled',
      metadata: {
        enabled: !sortByDate
      }
    });
    setSortByDate(!sortByDate);
  };
  
  return (
    <BaseToggleButton
      isActive={sortByDate}
      onClick={handleToggle}
      icon={ArrowUpDown}
      label="Sort by Date"
      activeLabel="Newest First"
    />
  );
};

// Filter Panel Toggle Button
export const FilterPanelToggle = () => {
  const { showFilterPanel, setShowFilterPanel } = usePlanningContext();
  
  const handleToggle = () => {
    setShowFilterPanel(!showFilterPanel);
  };
  
  return (
    <BaseToggleButton
      isActive={showFilterPanel}
      onClick={handleToggle}
      icon={Filter}
      label="More Filters"
      activeLabel="Hide Filters"
    />
  );
};

// Grouped Applications Only Toggle Button
export const GroupedApplicationsToggle = ({ large = false }) => {
  const { showOnlyGroupedApplications, setShowOnlyGroupedApplications } = usePlanningContext();
  
  const groupedButtonClasses = `btn flex items-center gap-2 ${large ? 'py-3 px-5 text-base' : ''} ${
    showOnlyGroupedApplications
      ? 'btn--primary'
      : 'bg-[var(--c-surface)] text-[var(--c-text-primary)] border-[var(--c-border)] hover:bg-[var(--c-surface-hover)]'
  }`;

  return (
    <motion.button
      whileTap={{ scale: 0.97 }}
      whileHover={{ scale: 1.03 }}
      className={groupedButtonClasses}
      onClick={() => {
        trackPlanningUpdatesEvent('planning_updates_grouped_filter_clicked', {
          placement: large ? 'display_options_large' : 'display_options',
          status: showOnlyGroupedApplications ? 'disabled' : 'enabled',
          metadata: {
            enabled: !showOnlyGroupedApplications
          }
        });
        setShowOnlyGroupedApplications(prev => !prev);
      }}
      title="Show only applications that have been matched with others (2+ apps per group)"
    >
      <Layers size={large ? 20 : 16} className="shrink-0" />
      <span className="font-medium">
        {showOnlyGroupedApplications ? 'Grouped Only' : 'Show Matched'}
      </span>
    </motion.button>
  );
};

// ViewModeToggle (exports individually instead of being included in export set)
export const ViewModeToggle = () => {
  // Implementation for ViewModeToggle...
  // This would be similar to the BaseToggleButton but with multiple options
  return null; // Placeholder
};
